import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const RECOVERY_FLAG_KEY = 'lead-tracker-password-recovery';
const RECOVERY_MAX_AGE_MS = 10 * 60 * 1000;

function readRecoveryMarker() {
  try {
    const raw = sessionStorage.getItem(RECOVERY_FLAG_KEY);
    if (!raw) return null;
    const marker = JSON.parse(raw);
    if (!marker?.userId || !Number.isFinite(marker?.createdAt)) return null;
    if (Date.now() - marker.createdAt > RECOVERY_MAX_AGE_MS) {
      sessionStorage.removeItem(RECOVERY_FLAG_KEY);
      return null;
    }
    return marker;
  } catch {
    sessionStorage.removeItem(RECOVERY_FLAG_KEY);
    return null;
  }
}

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    let active = true;
    let recoveryObserved = false;

    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' && session?.user?.id) {
        recoveryObserved = true;
        sessionStorage.setItem(RECOVERY_FLAG_KEY, JSON.stringify({
          userId: session.user.id,
          createdAt: Date.now(),
        }));
        if (active) {
          setError('');
          setReady(true);
        }
      }
    });

    async function initializeRecovery() {
      try {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');

        if (code) {
          const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
          if (exchangeError) throw exchangeError;
          window.history.replaceState({}, document.title, window.location.pathname);
        }

        await new Promise((resolve) => setTimeout(resolve, 350));
        const { data, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) throw sessionError;
        const marker = readRecoveryMarker();
        if (!data.session?.user?.id || !marker || marker.userId !== data.session.user.id || !recoveryObserved && !marker) {
          throw new Error('A verified password-recovery session is required.');
        }

        if (active) setReady(true);
      } catch (recoveryError) {
        console.error(recoveryError);
        sessionStorage.removeItem(RECOVERY_FLAG_KEY);
        if (active) setError('This password reset link is invalid or has expired. Request a new one.');
      }
    }

    initializeRecovery();
    return () => {
      active = false;
      authListener.subscription.unsubscribe();
    };
  }, []);

  async function submit(event) {
    event.preventDefault();
    setError('');
    setMessage('');

    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    const marker = readRecoveryMarker();
    if (sessionError || !sessionData.session?.user?.id || !marker || marker.userId !== sessionData.session.user.id) {
      sessionStorage.removeItem(RECOVERY_FLAG_KEY);
      setReady(false);
      setError('This password reset session is no longer valid. Request a new reset link.');
      return;
    }

    if (password.length < 12) {
      setError('Use a password with at least 12 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('The passwords do not match.');
      return;
    }

    setSaving(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setSaving(false);

    if (updateError) {
      console.error(updateError);
      setError('We could not update your password. Please request a new reset link and try again.');
      return;
    }

    sessionStorage.removeItem(RECOVERY_FLAG_KEY);
    setMessage('Password updated successfully.');
    setPassword('');
    setConfirmPassword('');
  }

  if (!ready && !error) {
    return <div className="center-screen"><div className="spinner" /></div>;
  }

  return (
    <div className="center-screen login-background">
      <form className="auth-card" onSubmit={submit}>
        <div className="brand-mark">LT</div>
        <h1>Set new password</h1>
        <p>Choose a new password for your Lead Tracker account.</p>

        {ready && (
          <>
            <label className="field-label" htmlFor="new-password">New password</label>
            <input
              id="new-password"
              className="text-input"
              type="password"
              autoComplete="new-password"
              minLength={12}
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="At least 12 characters"
            />

            <label className="field-label" htmlFor="confirm-password">Confirm password</label>
            <input
              id="confirm-password"
              className="text-input"
              type="password"
              autoComplete="new-password"
              minLength={12}
              required
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              placeholder="Re-enter your password"
            />
          </>
        )}

        {message && <div className="success-message">{message}</div>}
        {error && <div className="error-message">{error}</div>}

        {ready && !message && (
          <button className="primary-button" disabled={saving} type="submit">
            {saving ? 'Updating…' : 'Update password'}
          </button>
        )}

        <button
          className="secondary-button"
          type="button"
          onClick={async () => {
            sessionStorage.removeItem(RECOVERY_FLAG_KEY);
            if (message) await supabase.auth.signOut();
            navigate('/login', { replace: true });
          }}
          style={{ width: '100%', marginTop: '8px' }}
        >
          {message ? 'Return to login' : 'Back to login'}
        </button>
      </form>
    </div>
  );
}
